import type { ApiResponse } from '@/types'

class ApiClient {
  private baseURL: string
  private defaultHeaders: Record<string, string>
  private tokenRefreshCallback?: () => Promise<string | null>
  private forbiddenCallback?: () => void
  private isForbiddenRedirecting = false

  constructor(baseURL: string = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001/api') {
    this.baseURL = baseURL
    this.defaultHeaders = {
      'Content-Type': 'application/json'
    }
  }

  setAuthToken(token: string) {
    this.defaultHeaders['Authorization'] = `Bearer ${token}`
  }

  removeAuthToken() {
    delete this.defaultHeaders['Authorization']
  }

  setTokenRefreshCallback(callback: () => Promise<string | null>) {
    this.tokenRefreshCallback = callback
  }

  setForbiddenCallback(callback: () => void) {
    this.forbiddenCallback = callback
  }

  private async request<T = any>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`

    const config: RequestInit = {
      ...options,
      headers: {
        ...this.defaultHeaders,
        ...options.headers
      }
    }

    try {
      const response = await fetch(url, config)

      // Se a resposta for 401 (não autorizado) e tivermos callback de refresh, tenta renovar o token
      if (response.status === 401 && this.tokenRefreshCallback) {
        try {
          const newToken = await this.tokenRefreshCallback()
          if (newToken) {
            // Refaz a requisição com o novo token
            config.headers = {
              ...this.defaultHeaders,
              ...options.headers
            }
            const retryResponse = await fetch(url, config)

            if (!retryResponse.ok) {
              throw new Error(`HTTP ${retryResponse.status}: ${retryResponse.statusText}`)
            }

            const data = await retryResponse.json()
            return {
              success: true,
              ...data,
              message: 'Success'
            }
          }
        } catch (refreshError) {
          console.error('Erro ao renovar token:', refreshError)
          // Se falhar ao renovar, continua com o erro original 401
        }
      }

      // Se a resposta for 403 (proibido), redireciona para login
      if (response.status === 403 && this.forbiddenCallback && !this.isForbiddenRedirecting) {
        this.isForbiddenRedirecting = true
        this.forbiddenCallback()
        setTimeout(() => { this.isForbiddenRedirecting = false }, 3000)
        return {
          success: false,
          error: 'Acesso negado',
          message: 'Acesso negado'
        }
      } else if (response.status === 403) {
        return {
          success: false,
          error: 'Acesso negado',
          message: 'Acesso negado'
        }
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()

      return {
        success: true,
        ...data,
        message: 'Success'
      }
    } catch (error: any) {
      console.error('API Request failed:', error)
      return {
        success: false,
        error: error.message || 'Request failed',
        message: 'Request failed'
      }
    }
  }

  async get<T = any>(endpoint: string, params?: Record<string, any>): Promise<ApiResponse<T>> {
    const url = params ? `${endpoint}?${new URLSearchParams(params).toString()}` : endpoint
    return this.request<T>(url, { method: 'GET' })
  }

  async post<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    })
  }

  async put<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined
    })
  }

  async delete<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      body: data ? JSON.stringify(data) : undefined
    })
  }

  async patch<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined
    })
  }
}

export const apiClient = new ApiClient()
